/* eslint-disable no-param-reassign */
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as DynamicRankingLineLayerSpec } from '../../../../layer/preset/line/dynamicRankingLine';
import * as utils from '../../../../util';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';
import type { Data } from '../../specProcessor';

export type Spec = BaseSpec;

type DynamicRankingLineLayerType = StandardChartLayerSpec &
  DynamicRankingLineLayerSpec &
  ILayerTypeSpec;

// 创建动态排名折线图数据类型验证器
const validateDynamicRankingLineDataTypes = createDataTypeValidator('dynamicRankingLine', [
  { field: 'x', type: 'date' },
  { field: 'y', type: 'number' },
  { field: 'z', type: 'string' }
]);

// 创建动态排名折线图编码验证器
const validateDynamicRankingLineEncoding = createEncodingValidator([
  { field: 'x' },
  { field: 'y' },
  { field: 'z' }
]);

// 额外的检查函数
const additionalDynamicRankingLineChecks = (layer: DynamicRankingLineLayerType, data: Data) => {
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  // 检查数据量是否符合要求
  if (values.length <= 1) {
    utils.logger.warn('warning', `The "${layer.type}" layer requires more than 1 data point`);
    return;
  }

  // 检查x轴数据量是否大于2
  const xValues = new Set(values.map(item => item[layer.encoding.x]));
  if (xValues.size <= 2) {
    utils.logger.warn('warning', `The "${layer.type}" layer requires more than 2 unique x values`);
    return;
  }

  // 检查y轴数据量是否大于1
  const yValues = new Set(values.map(item => item[layer.encoding.y]));
  if (yValues.size <= 1) {
    utils.logger.warn('warning', `The "${layer.type}" layer requires more than 1 unique y value`);
    return;
  }

  // 检查z轴数据量是否大于3
  const zValues = new Set(values.map(item => item[layer.encoding.z]));
  if (zValues.size <= 3) {
    utils.logger.warn(
      'warning',
      `The "${layer.type}" layer requires more than 3 unique z values for better visualization`
    );
    return;
  }

  // 检查潜规则：时间点数据集是否完整
  const { x, z } = layer.encoding;
  const timePoints = [...new Set(values.map(item => item[x]))];
  const categories = [...new Set(values.map(item => item[z]))];

  // 检查每个时间点是否都有所有分类的数据
  let hasIncompleteData = false;
  timePoints.forEach(time => {
    const timeData = values.filter(item => item[x] === time);
    const timeCategories = new Set(timeData.map(item => item[z]));

    if (timeCategories.size < categories.length) {
      hasIncompleteData = true;
    }
  });

  if (hasIncompleteData) {
    utils.logger.warn(
      'warning',
      `For best results, ensure all categories have data for each time point in "${layer.type}" layer`
    );
  }
};

// 创建并导出动态排名折线图处理器
export const dynamicRankingLine = createLayerProcessor<DynamicRankingLineLayerType>(
  'dynamicRankingLine',
  validateDynamicRankingLineEncoding,
  validateDynamicRankingLineDataTypes,
  additionalDynamicRankingLineChecks
);
